// ==========================================================================
// Phân quyền theo VỊ TRÍ (position).
//
// Nguồn sự thật khi chạy = collection `position` trên Firestore, chỉnh bằng
// màn hình /fox-admin/settings/position. File này chỉ còn giữ:
//   1. DANH MỤC FEATURE (FEATURES) — các ô để tick trong ma trận phân quyền;
//   2. QUYỀN MẶC ĐỊNH (ROUTE_ROLES / EDIT_ROLES) — dùng seed 3 vị trí gốc lần
//      đầu, và làm phương án dự phòng khi chưa đọc được Firestore.
//
// Thêm màn hình mới: thêm 1 dòng vào FEATURES + đặt `data: { feature: '<key>' }`
// cho route. Không phải sửa gì thêm ở đây.
// ==========================================================================

import { PermissionSnapshot } from '../models/position.model';

/** Giá trị users.role = document id của vị trí. '1'/'2'/'3' là 3 vị trí gốc. */
export type Role = string;

/** Id 3 vị trí gốc — trùng giá trị role cũ nên không phải migrate tài khoản. */
export const SYSTEM_POSITIONS = {
  ADMIN: '1',
  STAFF: '2',
  MANAGER: '3',
};

/** Key cache quyền của phiên đăng nhập hiện tại trong localStorage. */
export const PERM_CACHE_KEY = 'perm';

/**
 * Phiên bản của bảng quyền mặc định bên dưới.
 *
 * `PositionService.ensureSeeded()` so số này với `seed_version` của 3 vị trí
 * GỐC: cũ hơn thì ghi đè lại view/edit/field theo bảng mặc định. Nhờ vậy đổi
 * bảng trong file này là tự áp dụng xuống Firestore, không phải sửa tay.
 *
 * ⚠️ Tăng số này = XOÁ mọi chỉnh tay mà Admin đã làm trên 3 vị trí gốc. Chỉ
 * tăng khi thật sự muốn reset chúng về mặc định. Vị trí do Admin tự tạo không
 * bao giờ bị đụng tới.
 */
export const SEED_VERSION = 4;

/** Một dòng trong ma trận phân quyền. */
export interface FeatureDef {
  key: string;
  label: string;
  group: string;
  /**
   * Trang tương ứng, dùng để chọn nơi "hạ cánh" sau khi đăng nhập cho vị trí
   * không xem được Dashboard. Bỏ trống với các mục không mở trực tiếp được
   * (trang bọc, trang in cần :orderId...).
   */
  url?: string;
}

/**
 * DANH MỤC FEATURE — mỗi dòng là 1 màn hình/chức năng bật tắt được Xem/Sửa.
 * Thứ tự ở đây chính là thứ tự hiển thị trong ma trận.
 */
export const FEATURES: FeatureDef[] = [
  { key: 'dashboard', label: 'Dashboard', group: 'Bán hàng', url: '/fox-admin' },
  { key: 'order', label: 'Tạo hóa đơn', group: 'Bán hàng', url: '/fox-admin/order' },
  { key: 'order-list', label: 'Danh sách hóa đơn', group: 'Bán hàng', url: '/fox-admin/order-list' },
  { key: 'print', label: 'In hóa đơn', group: 'Bán hàng' },
  { key: 'mobile', label: 'Mobile/POS', group: 'Bán hàng', url: '/fox-admin/mobile' },

  { key: 'stringing', label: 'Phiếu đan vợt', group: 'Dịch vụ', url: '/fox-admin/racket-stringing' },
  { key: 'svc', label: 'Trang dịch vụ', group: 'Dịch vụ', url: '/fox-admin/services' },

  { key: 'product', label: 'Sản phẩm', group: 'Sản phẩm', url: '/fox-admin/settings/product/list' },
  { key: 'ptype', label: 'Loại sản phẩm', group: 'Sản phẩm', url: '/fox-admin/settings/product-type' },
  { key: 'branch', label: 'Hãng', group: 'Sản phẩm', url: '/fox-admin/settings/branch' },
  { key: 'supplier', label: 'Nhà cung cấp', group: 'Sản phẩm', url: '/fox-admin/settings/supplier' },
  { key: 'bulk-price', label: 'Sửa giá hàng loạt', group: 'Sản phẩm', url: '/fox-admin/settings/product/bulk-price' },

  { key: 'customer', label: 'Khách hàng', group: 'Khách & nhân sự', url: '/fox-admin/settings/customer/list' },

  // Chấm công & đăng ký giờ làm part-time.
  //   shift      — nhân viên tự đăng ký ca cho CHÍNH MÌNH (Sửa = đăng ký được).
  //   attendance — tự chấm công vào/ra.
  //   timesheet  — bảng công tháng, chốt lịch, sửa chấm công của NGƯỜI KHÁC,
  //                và cấu hình ca làm. Đây là ô tick dành cho Quản lý.
  { key: 'shift', label: 'Lịch làm việc (đăng ký ca)', group: 'Khách & nhân sự', url: '/fox-admin/work-schedule' },
  { key: 'attendance', label: 'Chấm công', group: 'Khách & nhân sự', url: '/fox-admin/attendance' },
  { key: 'timesheet', label: 'Bảng công & ca làm', group: 'Khách & nhân sự', url: '/fox-admin/timesheet' },

  { key: 'home-cfg', label: 'Trang chủ (Cover)', group: 'Nội dung web', url: '/fox-admin/home-config' },
  { key: 'about-cfg', label: 'Giới thiệu', group: 'Nội dung web', url: '/fox-admin/about-config' },
  { key: 'news', label: 'Tin tức', group: 'Nội dung web', url: '/fox-admin/news' },
  { key: 'flash', label: 'Flash Sale', group: 'Nội dung web', url: '/fox-admin/flash-sale' },
  { key: 'contact-cfg', label: 'Liên hệ', group: 'Nội dung web', url: '/fox-admin/contact-config' },

  { key: 'duck-race', label: 'Đua vịt & kho quà', group: 'Đua vịt', url: '/fox-admin/duck-race' },

  { key: 'settings', label: 'Trang cài đặt', group: 'Hệ thống' },
  { key: 'user', label: 'Tài khoản đăng nhập', group: 'Hệ thống', url: '/fox-admin/settings/user' },
  { key: 'position', label: 'Vị trí & phân quyền', group: 'Hệ thống', url: '/fox-admin/settings/position' },
];

/** Gom FEATURES theo nhóm, giữ đúng thứ tự xuất hiện. */
export function featureGroups(): { group: string; items: FeatureDef[] }[] {
  const out: { group: string; items: FeatureDef[] }[] = [];
  for (const f of FEATURES) {
    let bucket = out.find((g) => g.group === f.group);
    if (!bucket) {
      bucket = { group: f.group, items: [] };
      out.push(bucket);
    }
    bucket.items.push(f);
  }
  return out;
}

/**
 * VIEW mặc định — seed vị trí gốc & dự phòng khi chưa đọc được Firestore.
 * Chốt theo bảng docs/phan-quyen-role.xlsx (2026-09-29).
 */
export const ROUTE_ROLES: { [feature: string]: Role[] } = {
  dashboard: ['1', '3'], // Nhân viên không xem doanh thu tổng
  'order-list': ['1', '2', '3'],
  order: ['1', '2', '3'],
  print: ['1', '2', '3'],
  mobile: ['1', '2', '3'],
  stringing: ['1', '2', '3'],
  svc: ['1', '2', '3'],
  'home-cfg': ['1', '2', '3'],
  'about-cfg': ['1', '2', '3'],
  'contact-cfg': ['1', '2', '3'],
  news: ['1', '2', '3'],
  flash: ['1', '2', '3'],
  settings: ['1', '2', '3'],
  product: ['1', '2', '3'],
  'bulk-price': ['1', '2', '3'],
  ptype: ['1', '2', '3'],
  supplier: ['1'], // chỉ Admin
  customer: ['1', '2', '3'],
  // Cả shop phải xem được lịch & chấm công của nhau — đó là mục đích của tính năng.
  shift: ['1', '2', '3'],
  attendance: ['1', '2', '3'],
  timesheet: ['1', '3'], // bảng công: Admin + Quản lý
  user: ['1'], // chỉ Admin
  position: ['1'], // chỉ Admin
  branch: ['1', '2', '3'],
  'duck-race': ['1', '2', '3'],
};

/**
 * EDIT mặc định — seed vị trí gốc & dự phòng khi chưa đọc được Firestore.
 * Chốt theo bảng docs/phan-quyen-role.xlsx (2026-09-29).
 */
export const EDIT_ROLES: { [feature: string]: Role[] } = {
  dashboard: ['1'],
  'order-list': ['1', '2', '3'],
  order: ['1', '2', '3'],
  print: ['1', '2', '3'],
  mobile: ['1', '2', '3'],
  stringing: ['1', '2', '3'],
  svc: ['1', '2', '3'],
  'home-cfg': ['1', '3'],
  'about-cfg': ['1', '3'],
  'contact-cfg': ['1'],
  news: ['1', '2', '3'],
  flash: ['1', '3'],
  settings: ['1'],
  product: ['1'], // giá bán chỉ Admin sửa
  'bulk-price': ['1'],
  ptype: ['1', '3'],
  supplier: ['1'],
  customer: ['1', '2', '3'],
  // Mọi vị trí đều TỰ đăng ký ca và TỰ chấm công cho mình; việc chốt lịch /
  // sửa công của người khác nằm ở 'timesheet'. Rules cũng chặn theo đúng ranh
  // giới này nên nhân viên không ghi được dòng của người khác.
  shift: ['1', '2', '3'],
  attendance: ['1', '2', '3'],
  timesheet: ['1', '3'],
  user: ['1'],
  position: ['1'],
  branch: ['1', '3'],
  'duck-race': ['1', '3'],
};

// ==========================================================================
// QUYỀN THEO TRƯỜNG DỮ LIỆU (field-level)
//
// Khác với FEATURES (bật/tắt cả màn hình), phần này ẩn vài CỘT/Ô nhạy cảm
// ngay trong màn hình mà vị trí đó vẫn được xem. Dùng cho yêu cầu kiểu
// "nhân viên xem được sản phẩm nhưng không thấy giá nhập".
// ==========================================================================

/** Danh mục trường nhạy cảm — hiện thành ô tick trong màn Vị trí. */
export const FIELDS: FeatureDef[] = [
  {
    key: 'cost-price',
    label: 'Giá nhập / giá vốn',
    group: 'Dữ liệu nhạy cảm',
  },
  { key: 'profit', label: 'Lợi nhuận', group: 'Dữ liệu nhạy cảm' },
];

/** FIELD mặc định — mặc định chỉ Admin thấy giá nhập và lợi nhuận. */
export const FIELD_ROLES: { [field: string]: Role[] } = {
  'cost-price': ['1'],
  profit: ['1'],
};

/**
 * Danh sách key mặc định của 1 vị trí gốc, suy ra từ các bảng trên.
 * Duyệt thẳng key của `table` nên dùng được cho cả FEATURES lẫn FIELDS.
 */
export function defaultFeaturesFor(
  role: Role,
  table: { [feature: string]: Role[] }
): string[] {
  return Object.keys(table).filter(
    (key) => (table[key] || []).indexOf(role) !== -1
  );
}

/**
 * Đọc ảnh chụp quyền đang cache. Trả null nếu chưa đăng nhập / chưa tải xong.
 * Đặt ở đây (không nằm trong service) vì `canEditFeature` là hàm thuần, được
 * gọi thẳng trong component nơi không inject được service.
 */
export function readPermissionSnapshot(): PermissionSnapshot | null {
  try {
    const raw = localStorage.getItem(PERM_CACHE_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as PermissionSnapshot;
    return snap && Array.isArray(snap.view) ? snap : null;
  } catch {
    return null;
  }
}

/**
 * Dự phòng khi chưa có cache (vd vừa F5 trước lúc service kịp tải): dùng bảng
 * mặc định theo role trong localStorage. Vị trí tự tạo không nằm trong bảng
 * này nên sẽ ra "không có quyền" — đúng ý: chưa biết thì chưa cho.
 */
function fallbackAllows(
  feature: string,
  table: { [f: string]: Role[] }
): boolean {
  const role = (localStorage.getItem('role') ?? '').toString();
  const allowed = table[feature];
  return !!allowed && allowed.indexOf(role) !== -1;
}

/** Vị trí hiện tại có được XEM feature này không. */
export function canViewFeature(feature: string): boolean {
  const snap = readPermissionSnapshot();
  if (!snap) return fallbackAllows(feature, ROUTE_ROLES);
  return snap.is_admin || snap.view.indexOf(feature) !== -1;
}

/** Vị trí hiện tại có được SỬA feature này không (dùng trong component). */
export function canEditFeature(feature: string): boolean {
  const snap = readPermissionSnapshot();
  if (!snap) return fallbackAllows(feature, EDIT_ROLES);
  return snap.is_admin || snap.edit.indexOf(feature) !== -1;
}

/**
 * Trang đầu tiên vị trí hiện tại mở được, theo đúng thứ tự FEATURES.
 *
 * Dùng làm nơi "hạ cánh" sau khi đăng nhập và khi guard chặn một route: không
 * phải vị trí nào cũng xem được Dashboard (ví dụ Nhân viên), mà mặc định
 * `/fox-admin` CHÍNH LÀ Dashboard — cứ đá về đó thì guard lại chặn tiếp rồi
 * đẩy ra trang đăng nhập, thành vòng lặp khoá luôn người dùng ở ngoài.
 *
 * Trả về null nếu vị trí không mở được trang nào (khi đó mới về trang login).
 */
export function firstAllowedRoute(): string | null {
  for (const f of FEATURES) {
    if (f.url && canViewFeature(f.key)) return f.url;
  }
  return null;
}

/**
 * Vị trí hiện tại có được XEM trường nhạy cảm này không ('cost-price',
 * 'profit'). Dùng để ẩn cột giá nhập / lợi nhuận ngay trong màn hình mà vị
 * trí đó vẫn mở được.
 *
 * `field` là mảng mới, vị trí tạo trước khi có tính năng này sẽ không có →
 * coi như KHÔNG được xem (trừ Admin). An toàn hơn là mặc định cho xem.
 */
export function canViewField(field: string): boolean {
  const snap = readPermissionSnapshot();
  if (!snap) return fallbackAllows(field, FIELD_ROLES);
  return snap.is_admin || (snap.field || []).indexOf(field) !== -1;
}
